import React from 'react'
import { motion } from 'framer-motion'
import styles from './mental-health.module.css'
import LocationIcon from '@/components/IconsTsx/LocationIcon'
import PhoneIcon from '@/components/IconsTsx/GrayPhoneIcon'
import InfoIcon from '@/components/IconsTsx/InfoIcon'
import { isMinisterioSeguridadPlan } from '@/lib/planFilters'

interface ServiceLocation {
    id: string
    name: string
    location?: string
    phone: string
    info?: string
    email?: string
    whatsapp?: string
}

interface ZoneGroup {
    zoneName: string
    services: ServiceLocation[]
}

interface MentalHealthProps {
    /** Plan elegido (código CARTILLA). MS1/MS2 muestran solo su bloque de contacto. */
    plan?: string
}

const MailIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="m3 7 9 6 9-6" />
    </svg>
)

const WhatsAppIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.004c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm5.8 14.13c-.24.68-1.4 1.3-1.94 1.34-.5.05-1.13.2-3.66-.78-3.08-1.21-5.05-4.34-5.2-4.54-.15-.2-1.24-1.65-1.24-3.15 0-1.5.79-2.24 1.07-2.54.28-.3.6-.38.8-.38.2 0 .4 0 .58.01.19.01.44-.07.68.52.24.6.83 2.07.9 2.22.07.15.12.32.02.52-.1.2-.15.32-.3.5-.15.17-.32.39-.45.52-.15.15-.3.31-.13.6.17.3.76 1.25 1.63 2.03 1.12 1 2.06 1.31 2.36 1.46.3.15.47.12.64-.07.17-.2.74-.86.94-1.16.2-.3.4-.25.66-.15.27.1 1.72.81 2.02.96.3.15.5.22.57.34.07.12.07.7-.17 1.38Z" />
    </svg>
)

// Bloque único para MS1/MS2 (Ministerio de Seguridad): sin desglose por zonas
// ni Vitas, solo el contacto de Salud mental min.seg.
const MS_ZONE_GROUPS: ZoneGroup[] = [
    {
        zoneName: "GBA - CABA - Interior del país",
        services: [
            {
                id: "ms-salud-mental",
                name: "Salud mental",
                phone: "5288-4700",
                email: "Saludmental.min.seg@medicus.com.ar",
                whatsapp: "+54 9 11 5094-1119",
            },
        ],
    },
]

const MentalHealth: React.FC<MentalHealthProps> = ({ plan }) => {
    // Función para normalizar nombres de servicios
    const normalizeServiceName = (name: string): string => {
        // Casos específicos de normalización
        if (name.toLowerCase() === "centro de programas especiales") {
            return "Centro de Programas Especiales"
        }
        
        // Normalización general: capitalizar primera letra de cada palabra
        return name
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
    }

    // Función para manejar la llamada telefónica
    const handleCall = (phoneNumber: string) => {
        // Limpiar el número de teléfono (remover espacios, guiones, etc.)
        const cleanNumber = phoneNumber.replace(/[\s\-\(\)]/g, '');
        // Crear el enlace tel: para hacer la llamada
        window.location.href = `tel:${cleanNumber}`;
    };

    const handleEmail = (email: string) => {
        window.location.href = `mailto:${email}`;
    };

    const handleWhatsApp = (whatsapp: string) => {
        const digits = whatsapp.replace(/\D/g, '');
        window.open(`https://wa.me/${digits}`, '_blank');
    };

    // Datos reorganizados por zonas
    const zoneGroups: ZoneGroup[] = [
        {
            zoneName: "CABA",
            services: [
                {
                    id: "1",
                    name: "Centro de programas especiales",
                    location: "Juncal 2194",
                    phone: "5288-4700"
                }
            ]
        },
        {
            zoneName: "Interior del país",
            services: [
                {
                    id: "2",
                    name: "Vitas Salud Mental",
                    phone: "0800-345-1298",
                }
            ]
        },
        {
            zoneName: "GBA - Zona Norte",
            services: [
                {
                    id: "3",
                    name: "Centro de programas especiales",
                    phone: "5288-4700",
                },
                {
                    id: "5",
                    name: "Vitas Salud Mental",
                    info: "Para asociados que residan en Capilla del Señor, Exaltación de la Cruz",
                    phone: "0800-345-1298",
                }
            ]
        },
        {
            zoneName: "GBA - Zona Sudoeste",
            services: [
                {
                    id: "4",
                    name: "Centro de programas especiales",
                    phone: "5288-4700",
                },
                {
                    id: "6",
                    name: "Vitas Salud Mental",
                    info: "Para asociados que residan en Cañuelas",
                    phone: "0800-345-1298",
                }
            ]
        },
        {
            zoneName: "GBA - Zona Noroeste",
            services: [
                {
                    id: "7",
                    name: "Centro de programas especiales",
                    phone: "5288-4700",
                },
                {
                    id: "9",
                    name: "Vitas Salud Mental",
                    info: "Para asociados que residan en Gral. Rodríguez, Las Heras o Luján",
                    phone: "0800-345-1298",
                }
            ]
        },
        {
            zoneName: "GBA - Zona Sudeste",
            services: [
                {
                    id: "8",
                    name: "Centro de programas especiales",
                    phone: "5288-4700",
                },
                {
                    id: "10",
                    name: "Vitas Salud Mental",
                    info: "Para asociados que residan en La Plata",
                    phone: "0800-345-1298",
                }
            ]
        },
        {
            zoneName: "GBA - Zona Oeste",
            services: [
                {
                    id: "11",
                    name: "Centro de programas especiales",
                    phone: "5288-4700",
                },
                {
                    id: "12",
                    name: "Vitas Salud Mental",
                    info: "Para asociados que residan en Campana y Zárate",
                    phone: "0800-345-1298",
                }
            ]
        }
    ]

    // MS1/MS2 → solo el bloque min.seg; planes tradicionales → zonas con Vitas.
    const groups = isMinisterioSeguridadPlan(plan) ? MS_ZONE_GROUPS : zoneGroups

    return (
        <motion.div
            className={styles.container}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
        >
            <motion.div 
                className={styles.header}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
            >
                <h1 className={styles.title}>
                    Psicología médica y psiquiátrica <span className={styles.titleCount}>(1)</span>
                </h1>
                <div className={styles.emergency}>
                    Urgencias psiquiátricas Tel.: 4129-5300 | Las 24 hs., los 365 días del año.
                </div>
            </motion.div>

            <div className={styles.scrollContainer}>
                <motion.div 
                    className={styles.servicesGrid}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                >
                    {groups.map((zoneGroup, zoneIndex) => (
                        <motion.div 
                            key={zoneIndex} 
                            className={styles.zoneGroup}
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ 
                                duration: 0.5, 
                                delay: 0.6 + (zoneIndex * 0.1),
                                ease: "easeOut"
                            }}
                        >
                            <h2 className={styles.zoneTitle}>{zoneGroup.zoneName}</h2>
                            {zoneGroup.services.map((service, serviceIndex) => (
                                <motion.div 
                                    key={service.id} 
                                    className={styles.serviceCard}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ 
                                        duration: 0.4, 
                                        delay: 0.8 + (zoneIndex * 0.1) + (serviceIndex * 0.1),
                                        ease: "easeOut"
                                    }}
                                >
                                    <div className={styles.serviceName}>{normalizeServiceName(service.name)}</div>

                                    {service.location && (
                                        <div className={styles.serviceLocation}>
                                            <LocationIcon className={styles.locationIcon} />
                                            <span>{service.location}</span>
                                        </div>
                                    )}

                                    {service.info && (
                                        <div className={styles.serviceLocation}>
                                            <InfoIcon className={styles.locationIcon} />
                                            <span>{service.info}</span>
                                        </div>
                                    )}
                                    <div className={styles.servicePhone}>
                                        <PhoneIcon className={styles.phoneIcon} />
                                        <span>{service.phone}</span>
                                    </div>

                                    {service.email && (
                                        <div className={styles.servicePhone}>
                                            <MailIcon />
                                            <span>{service.email}</span>
                                        </div>
                                    )}

                                    {service.whatsapp && (
                                        <div className={styles.servicePhone}>
                                            <WhatsAppIcon />
                                            <span>{service.whatsapp}</span>
                                        </div>
                                    )}

                                    <div className={styles.serviceActions}>
                                        <button
                                            className={styles.callButton}
                                            onClick={() => handleCall(service.phone)}
                                        >
                                            Llamar
                                        </button>

                                        {service.whatsapp && (
                                            <button
                                                className={styles.callButton}
                                                onClick={() => handleWhatsApp(service.whatsapp!)}
                                            >
                                                WhatsApp
                                            </button>
                                        )}

                                        {service.email && (
                                            <button
                                                className={styles.callButton}
                                                onClick={() => handleEmail(service.email!)}
                                            >
                                                Enviar mail
                                            </button>
                                        )}
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    ))}
                </motion.div>
                <motion.div 
                    className={styles.footer}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 1.2, ease: "easeOut" }}
                >
                    (1) Aranceles preferenciales y/o descuentos especiales
                </motion.div>
            </div>
        </motion.div>
    )
}

export default MentalHealth